// Mapa en vivo de los choferes. El GPS del PR 1 junta 77.000 posiciones por
// día y hasta ahora ninguna pantalla las mostraba: acá está la última posición
// de cada chofer, con su estado de seguimiento y hace cuánto reportó.
//
// Lo que un despachante necesita leer de un vistazo no es dónde está cada
// punto sino QUIÉN DEJÓ DE REPORTAR: un chofer en gris hace más de cinco
// minutos que no manda nada, y eso es lo primero que hay que mirar.
//
// Leaflet se carga en tiempo de ejecución desde el CDN, sin dependencia de
// npm: los mapas base ya vienen de internet (OpenStreetMap), así que el panel
// no gana nada empaquetando la librería y sí evita tocar el package-lock.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { useLeaflet } from "../hooks/useLeaflet";

type Chofer = {
  driver_id: number; username: string; first_name: string | null; last_name: string | null; vehiculo: string | null;
  latitude: number; longitude: number; accuracy_m: number | null; speed_mps: number | null; heading_deg: number | null;
  battery_level: number | null; recorded_at: string; received_at: string; source: string;
  tracking_mode: string | null; tracking_error: string | null; tracking_at: string | null;
  turbo_enabled?: boolean;
  // `turbo_enabled` es "puede recibir ofertas"; `turbo_route_id` es "lleva
  // uno AHORA". Son cosas distintas y el despachante mira la segunda.
  turbo_route_id?: number | null; turbo_deadline_at?: string | null;
  phone?: string | null;
};

const REFRESCO_MS = 10_000;
const SIN_SENAL_MIN = 5;
// Cuánto tardó la última posición en llegar desde que el teléfono la grabó.
// Más de esto es «subida con atraso»: el teléfono graba pero no puede mandar
// (red cortada en segundo plano); el punto se ve pero es viejo.
const ATRASO_MIN = 5;
const CENTRO: [number, number] = [-34.62, -58.45];

// Cuánto hace que el chofer reportó, en minutos.
const minutosDesde = (iso: string) => Math.max(0, (Date.now() - new Date(iso).getTime()) / 60000);
const atrasoMin = (c: { recorded_at: string; received_at: string }) =>
  Math.max(0, (new Date(c.received_at).getTime() - new Date(c.recorded_at).getTime()) / 60000);
const conAtraso = (c: { recorded_at: string; received_at: string }) => atrasoMin(c) > ATRASO_MIN;

const COLOR: Record<string, string> = { background: "#4f8cff", foreground: "#3fb950", heartbeat: "#d29922", buffer: "#4f8cff" };
const GRIS = "#6b7280";
const TURBO = "#f0883e";
const llevaTurbo = (c: Chofer) => c.turbo_route_id != null;
// Un chofer con turbo se pinta de turbo aunque esté sin señal: justamente
// ese es el caso que hay que ver primero (el punto igual se agranda y la
// lista lo sube arriba de todo).
const colorDe = (c: Chofer) => (llevaTurbo(c) ? TURBO : minutosDesde(c.recorded_at) > SIN_SENAL_MIN ? GRIS : COLOR[c.source] ?? "#4f8cff");
const soloDigitos = (t: string) => t.replace(/\D/g, "");
const horaCorta = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "";

const FUENTE: Record<string, string> = { background: "segundo plano", foreground: "app abierta", heartbeat: "quieto (latido)", buffer: "subido del buffer" };
const MODO: Record<string, string> = { background: "seguimiento en segundo plano", "foreground-only": "solo con la app abierta", denied: "sin permiso de ubicación", off: "GPS apagado" };

function haceCuanto(iso: string) {
  const m = minutosDesde(iso);
  if (m < 1) return "ahora";
  if (m < 60) return `hace ${Math.round(m)} min`;
  const h = Math.floor(m / 60);
  return `hace ${h} h ${Math.round(m % 60)} min`;
}

/* ===== Estilos (mismo lenguaje que PlantaPage.tsx) ===== */
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const kpiStyle: React.CSSProperties = { fontSize: "28px", fontWeight: 800, lineHeight: 1.1, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" };
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color });
const punto = (color: string): React.CSSProperties => ({ display: "inline-block", width: "10px", height: "10px", borderRadius: "50%", background: color, marginRight: "6px", verticalAlign: "middle" });
const linkBtnStyle: React.CSSProperties = { background: "none", border: "none", padding: 0, font: "inherit", fontSize: "12px", color: "var(--primary, #4f8cff)", cursor: "pointer", textDecoration: "underline" };
const filaStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "14px 1fr auto", gap: "10px", alignItems: "center", padding: "9px 6px", borderBottom: "1px solid var(--border)", cursor: "pointer", borderRadius: "8px" };

export default function MapaChoferesPage() {
  const { token } = useAuth();
  const leaflet = useLeaflet();
  const [choferes, setChoferes] = useState<Chofer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [soloActivos, setSoloActivos] = useState(false);
  const [actualizado, setActualizado] = useState<Date | null>(null);
  const [seleccionado, setSeleccionado] = useState<number | null>(null);
  const [aviso, setAviso] = useState<{ driverId: number; texto: string; ok: boolean } | null>(null);
  const [avisando, setAvisando] = useState<number | null>(null);
  const mapaRef = useRef<any>(null);
  const capaRef = useRef<any>(null);
  const contenedorRef = useRef<HTMLDivElement>(null);

  const cargar = useCallback(async () => {
    try {
      const res = await authFetch(`/v2/driver-locations/last${soloActivos ? `?active_minutes=${SIN_SENAL_MIN}` : ""}`, { token });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.ok === false) throw new Error(json?.error || `Error ${res.status}`);
      setChoferes(json.drivers ?? []);
      setActualizado(new Date());
      setError(null);
    } catch (e: any) { setError(e.message); }
  }, [token, soloActivos]);

  useEffect(() => { cargar(); const t = window.setInterval(cargar, REFRESCO_MS); return () => window.clearInterval(t); }, [cargar]);

  // El mapa se crea una vez; después solo se redibujan los puntos.
  useEffect(() => {
    if (!leaflet || !contenedorRef.current || mapaRef.current) return;
    const L = (window as any).L;
    const mapa = L.map(contenedorRef.current, { zoomControl: true }).setView(CENTRO, 10);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18, attribution: "© OpenStreetMap" }).addTo(mapa);
    mapaRef.current = mapa;
    capaRef.current = L.layerGroup().addTo(mapa);
  }, [leaflet]);

  // Se redibuja también cuando Leaflet termina de cargar: si los datos llegan
  // antes que la librería, sin esto los puntos recién aparecían en el
  // siguiente refresco, diez segundos después.
  useEffect(() => {
    const L = (window as any).L;
    if (!L || !capaRef.current) return;
    capaRef.current.clearLayers();
    for (const c of choferes) {
      if (c.latitude == null || c.longitude == null) continue;
      const nombre = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.username;
      const turbo = llevaTurbo(c);
      L.circleMarker([c.latitude, c.longitude], {
        radius: (seleccionado === c.driver_id ? 9 : 6) + (turbo ? 3 : 0),
        weight: turbo ? 3 : seleccionado === c.driver_id ? 3 : 1,
        color: turbo ? TURBO : "#111", fillOpacity: 0.95, fillColor: colorDe(c),
      })
        .bindTooltip(`${nombre}${turbo ? ` · TURBO #${c.turbo_route_id}${c.turbo_deadline_at ? ` (entrega ${horaCorta(c.turbo_deadline_at)})` : ""}` : ""} · ${haceCuanto(c.recorded_at)} · ${FUENTE[c.source] ?? c.source}${conAtraso(c) ? ` · subió con ${Math.round(atrasoMin(c))} min de atraso` : ""}`)
        .on("click", () => setSeleccionado(c.driver_id))
        .addTo(capaRef.current);
    }
  }, [choferes, seleccionado, leaflet]);

  const resumen = useMemo(() => {
    const enCalle = choferes.filter((c) => minutosDesde(c.recorded_at) <= SIN_SENAL_MIN).length;
    const sinSenal = choferes.length - enCalle;
    const foregroundOnly = choferes.filter((c) => c.tracking_mode && c.tracking_mode !== "background").length;
    const atrasados = choferes.filter((c) => minutosDesde(c.recorded_at) <= SIN_SENAL_MIN && conAtraso(c)).length;
    const conTurbo = choferes.filter(llevaTurbo).length;
    const turboSinSenal = choferes.filter((c) => llevaTurbo(c) && minutosDesde(c.recorded_at) > SIN_SENAL_MIN).length;
    return { enCalle, sinSenal, foregroundOnly, atrasados, conTurbo, turboSinSenal };
  }, [choferes]);

  // Primero el que lleva un turbo y encima no reporta: es el único caso que
  // pide hacer algo YA. Después el resto de los turbos, y después por
  // silencio como antes.
  const prioridad = (c: Chofer) => (llevaTurbo(c) ? (minutosDesde(c.recorded_at) > SIN_SENAL_MIN ? 0 : 1) : 2);
  const ordenados = useMemo(
    () => [...choferes].sort((a, b) => prioridad(a) - prioridad(b) || minutosDesde(b.recorded_at) - minutosDesde(a.recorded_at)),
    [choferes]
  );

  const avisarAlChofer = async (c: Chofer) => {
    setAvisando(c.driver_id);
    try {
      const res = await authFetch(`/v2/drivers/${c.driver_id}/ping`, { token, method: "POST", json: true, body: JSON.stringify({}) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.ok === false) throw new Error(json?.error || `Error ${res.status}`);
      setAviso(json.sent
        ? { driverId: c.driver_id, texto: "Aviso enviado al teléfono del chofer", ok: true }
        : { driverId: c.driver_id, texto: `No tiene la app registrada para recibir avisos${json.phone ? `: llamalo al ${json.phone}` : ""}`, ok: false });
    } catch (e: any) {
      setAviso({ driverId: c.driver_id, texto: e.message, ok: false });
    } finally { setAvisando(null); }
  };

  const centrarEn = (c: Chofer) => {
    setSeleccionado(c.driver_id);
    if (mapaRef.current && c.latitude != null) mapaRef.current.setView([c.latitude, c.longitude], Math.max(mapaRef.current.getZoom(), 13));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Choferes en vivo</h2>
            <p style={subtitleStyle}>
              La última posición de cada chofer. Lo que hay que mirar primero es el gris: hace más de {SIN_SENAL_MIN} minutos que no reporta.
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <Button variant={soloActivos ? "primary" : "secondary"} onClick={() => setSoloActivos((v) => !v)}>
              {soloActivos ? "Mostrando solo los que reportan" : "Mostrar solo los que reportan"}
            </Button>
            <Button variant="secondary" onClick={cargar}>Actualizar</Button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "14px", marginTop: "18px" }}>
          <div><div style={smallMutedStyle}>En la calle, reportando</div><div style={{ ...kpiStyle, color: "var(--success)" }}>{resumen.enCalle}</div></div>
          <div><div style={smallMutedStyle}>Sin señal hace más de {SIN_SENAL_MIN} min</div><div style={{ ...kpiStyle, color: resumen.sinSenal ? "var(--danger)" : "var(--text-primary)" }}>{resumen.sinSenal}</div></div>
          <div><div style={smallMutedStyle}>Reportando con atraso (más de {ATRASO_MIN} min)</div><div style={{ ...kpiStyle, color: resumen.atrasados ? "var(--warning, #d29922)" : "var(--text-primary)" }}>{resumen.atrasados}</div></div>
          <div><div style={smallMutedStyle}>Llevando un turbo</div><div style={{ ...kpiStyle, color: resumen.conTurbo ? TURBO : "var(--text-primary)" }}>{resumen.conTurbo}</div>{resumen.turboSinSenal ? <div style={{ ...smallMutedStyle, color: "var(--danger)" }}>{resumen.turboSinSenal} sin señal</div> : null}</div>
          <div><div style={smallMutedStyle}>Con la app sin permiso de fondo</div><div style={{ ...kpiStyle, color: resumen.foregroundOnly ? "var(--warning, #d29922)" : "var(--text-primary)" }}>{resumen.foregroundOnly}</div></div>
          <div><div style={smallMutedStyle}>Actualizado</div><div style={{ ...kpiStyle, fontSize: "18px" }}>{actualizado ? actualizado.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}</div><div style={smallMutedStyle}>cada {REFRESCO_MS / 1000} s</div></div>
        </div>
        {error ? <div style={{ marginTop: "12px", color: "var(--danger)" }}>{error}</div> : null}
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 1fr)", gap: "20px" }}>
        <Card>
          <div ref={contenedorRef} style={{ height: "560px", borderRadius: "12px", overflow: "hidden", background: "var(--surface-2, #0c0e12)" }}>
            {!leaflet ? <div style={{ ...smallMutedStyle, padding: "16px" }}>Cargando el mapa…</div> : null}
          </div>
          <div style={{ ...smallMutedStyle, marginTop: "10px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <span><i style={punto(COLOR.background)} />segundo plano</span>
            <span><i style={punto(COLOR.foreground)} />app abierta</span>
            <span><i style={punto(COLOR.heartbeat)} />quieto (latido)</span>
            <span><i style={punto(GRIS)} />sin señal hace más de {SIN_SENAL_MIN} min</span>
            <span><i style={punto(TURBO)} />llevando un turbo</span>
          </div>
        </Card>

        <Card>
          <div style={{ ...smallMutedStyle, marginBottom: "8px", fontWeight: 600 }}>
            {choferes.length} chofer{choferes.length === 1 ? "" : "es"} · los que hace más que no reportan, primero
          </div>
          <div style={{ maxHeight: "600px", overflowY: "auto" }}>
            {ordenados.length === 0 ? <div style={smallMutedStyle}>Ningún chofer reportó todavía.</div> : ordenados.map((c) => {
              const nombre = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.username;
              const sinSenal = minutosDesde(c.recorded_at) > SIN_SENAL_MIN;
              return (
                <div key={c.driver_id} style={{ ...filaStyle, background: seleccionado === c.driver_id ? "rgba(255,255,255,0.05)" : "transparent" }} onClick={() => centrarEn(c)}>
                  <i style={punto(colorDe(c))} />
                  <div>
                    <div style={{ color: "var(--text-primary)", fontSize: "14px", fontWeight: 600 }}>{nombre} <span style={{ ...smallMutedStyle, fontWeight: 400 }}>{c.username}{c.vehiculo ? ` · ${c.vehiculo}` : ""}</span>
                      {llevaTurbo(c) ? <span style={{ ...badge(TURBO), marginLeft: "8px", background: "rgba(240,136,62,0.12)", whiteSpace: "nowrap" }}>Turbo #{c.turbo_route_id}{c.turbo_deadline_at ? ` · ${horaCorta(c.turbo_deadline_at)}` : ""}</span> : null}
                      {c.turbo_enabled ? <span style={{ ...badge("var(--text-secondary)"), marginLeft: "8px", fontWeight: 500 }} title="Puede recibir ofertas de Turbo">habilitado</span> : null}
                    </div>
                    <div style={smallMutedStyle}>
                      {haceCuanto(c.recorded_at)} · {FUENTE[c.source] ?? c.source}
                      {conAtraso(c) ? <span style={{ color: "var(--warning, #d29922)" }}> · subió con {Math.round(atrasoMin(c))} min de atraso</span> : ""}
                      {c.battery_level != null ? ` · batería ${Math.round(c.battery_level * 100)}%` : ""}
                      {c.accuracy_m != null ? ` · ±${Math.round(c.accuracy_m)} m` : ""}
                    </div>
                    {c.tracking_mode && c.tracking_mode !== "background" ? (
                      <div style={{ ...smallMutedStyle, color: "var(--warning, #d29922)" }}>{MODO[c.tracking_mode] ?? c.tracking_mode}{c.tracking_error ? ` · ${c.tracking_error}` : ""}</div>
                    ) : null}
                    <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", marginTop: "4px" }} onClick={(e) => e.stopPropagation()}>
                      {c.phone ? (
                        <>
                          <a href={`tel:${soloDigitos(c.phone)}`} style={{ ...smallMutedStyle, color: "var(--primary, #4f8cff)" }}>{c.phone}</a>
                          <a href={`https://wa.me/${soloDigitos(c.phone)}`} target="_blank" rel="noreferrer" style={{ ...smallMutedStyle, color: "var(--primary, #4f8cff)" }}>WhatsApp</a>
                        </>
                      ) : <span style={smallMutedStyle}>sin teléfono cargado</span>}
                      <button type="button" style={linkBtnStyle} disabled={avisando === c.driver_id} onClick={() => avisarAlChofer(c)}>
                        {avisando === c.driver_id ? "avisando…" : "avisar al chofer"}
                      </button>
                    </div>
                    {aviso && aviso.driverId === c.driver_id ? (
                      <div style={{ ...smallMutedStyle, color: aviso.ok ? "var(--success)" : "var(--warning, #d29922)" }}>{aviso.texto}</div>
                    ) : null}
                  </div>
                  <span style={badge(sinSenal ? "var(--danger)" : conAtraso(c) ? "var(--warning, #d29922)" : "var(--success)")}>{sinSenal ? "sin señal" : conAtraso(c) ? "con atraso" : "ok"}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
